'use client';

import { BlogCard } from 'components/blog/blog-card';
import { BlogPagination } from 'components/blog/blog-pagination';
import { ShopifyArticle } from 'lib/shopify/blog';
import Link from 'next/link';
import { useMemo, useState } from 'react';

interface HomeBlogSectionProps {
  /** All articles from all blogs */
  allArticles: ShopifyArticle[];
  /** Section title */
  title?: string;
  /** Section subtitle */
  subtitle?: string;
  /** Additional CSS classes */
  className?: string;
}

// Number of articles per page (2 rows × 3 columns = 6)
const ARTICLES_PER_PAGE = 6;

/**
 * Home page blog section that displays all Pion Power blogs
 * with pagination in a 2x3 grid layout
 */
export function HomeBlogSection({
  allArticles,
  title = 'Pion Power Blogs',
  subtitle = 'Discover insights, guides, and stories about renewable energy solutions',
  className = ''
}: HomeBlogSectionProps) {
  const [currentPage, setCurrentPage] = useState(1);

  // Sort articles by publication date (newest first)
  const sortedArticles = useMemo(() => {
    // Filter out media blog articles, then sort by publication date
    return [...allArticles]
      .filter(article => article.blog?.handle !== 'media')
      .sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
  }, [allArticles]);

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(sortedArticles.length / ARTICLES_PER_PAGE));

  // Get articles for current page
  const currentArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
    return sortedArticles.slice(startIndex, startIndex + ARTICLES_PER_PAGE);
  }, [sortedArticles, currentPage]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of section for better UX
    document.getElementById('home-blog-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Don't render if no articles
  if (sortedArticles.length === 0) {
    return null;
  }

  return (
    <section id="home-blog-section" className={`bg-white py-16 ${className}`}>
      <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12">
          <div className="mb-8 flex flex-row items-start justify-between gap-4 sm:items-end">
            <div className="flex-1 min-w-0">
              <h2 className="mb-4 text-2xl lg:text-4xl lg:font-bold text-gray-900">{title}</h2>
              {subtitle && <p className="max-w-2xl text-base sm:text-lg text-gray-600">{subtitle}</p>}
            </div>
            <Link
              href="/about/blogs"
              className="inline-flex items-center flex-shrink-0 rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:px-6"
            >
              View All
            </Link>
          </div>
        </div>

        {/* Articles Grid - 2 columns on mobile, 2 on medium, 3 on large */}
        {currentArticles.length > 0 ? (
          <>
            <div className="mb-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
              {currentArticles.map((article) => (
                <BlogCard key={article.id} article={article} blogHandle={article.blog?.handle} />
              ))}
            </div>

            {/* Pagination */}
            <BlogPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="border-t border-gray-200 pt-8"
            />
          </>
        ) : (
          <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
            <p className="text-gray-500">No articles available yet. Check back soon!</p>
          </div>
        )}
      </div>
    </section>
  );
}
